import { useState } from 'react';
import styled from 'styled-components';

import { Label } from '../../../../../components/Toolkit/Inputs/Label';
import { SimpleDropdown } from '../../../../../components/Toolkit/SimpleDropdown/SimpleDropdown';
import { Menu } from '../../../../../components/Toolkit/Menu/Menu';
import { removeNonNumericCharacters } from '../../../../../helpers/forms';
import { useOnUpdateOnly } from '../../../../../hooks/UseOnUpdateOnly';
import { CURRENCIES } from '../../../../../types';
import {
  Select,
  StrictOption,
} from '../../../../../components/Toolkit/Inputs/Select';
import { ListSingleSelectTag } from '../../../../../components/DealerPage/components/Filters/ListSingleSelectTag/ListSingleSelectTag';
import { TypePrice } from '../../../../../api/types/searchPageApiTypes';
import { DropDownRangeContainer } from '../../../../../components/DealerPage/components/Filters/DropDownRange/DropDownRange';
import { mapOptionsRanges } from '../../../../../components/DealerPage/mappers/filters';

type DropDownValue = {
  options: StrictOption[];
  selectedValue: string;
};

export interface IPriceSelectRangeProps {
  value?: {
    from: string | undefined;
    to: string | undefined;
    currency: CURRENCIES | undefined;
    typePrice: TypePrice | undefined;
  };
  typePrice: TypePrice;
  onChangePrice: (
    currency: CURRENCIES,
    from: string | undefined,
    to: string | undefined,
    type: TypePrice,
  ) => void;
  onChangeType: (currency: CURRENCIES, type: TypePrice) => void;
  from: DropDownValue;
  to: DropDownValue;
  fromMonth: DropDownValue;
  toMonth: DropDownValue;
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TypeContainer = styled.div``;

type PriceCurrency = {
  value: CURRENCIES;
  displayName: string;
  symbol: string;
};

const euro: PriceCurrency = {
  value: CURRENCIES.EUR,
  displayName: '€ EUR',
  symbol: '€',
};

const pound: PriceCurrency = {
  value: CURRENCIES.GBP,
  displayName: '£ GBP',
  symbol: '£',
};

function PriceSelectMonthRange({
  value,
  onChangePrice,
  typePrice,
  from,
  to,
  fromMonth,
  toMonth,
  onChangeType,
}: IPriceSelectRangeProps) {
  const [currency, setCurrency] = useState<CURRENCIES>(
    value?.currency || CURRENCIES.EUR,
  );
  const [fromValue, setFrom] = useState<string | undefined>(value?.from);
  const [toValue, setTo] = useState<string | undefined>(value?.to);
  useOnUpdateOnly(() => {
    if (value?.from !== fromValue) {
      setFrom(value?.from);
    }
    if (value?.to !== toValue) {
      setTo(value?.to);
    }
    if (value?.currency !== currency) {
      setCurrency(value?.currency || CURRENCIES.EUR);
    }
  }, [value]);

  const priceCurrency = currency === CURRENCIES.GBP ? pound : euro;

  const handleOnCurrencyChange = (
    currency: CURRENCIES,
    typePrice: TypePrice,
  ) => {
    setCurrency(currency);
    onChangePrice(currency, fromValue, toValue, typePrice);
  };

  const isPricePerMonth = typePrice === 'pricePerMonth';

  return (
    <div>
      <HeaderContainer>
        <Label htmlFor="search-price-filter-for">Price</Label>
        <SimpleDropdown
          isDisabled={isPricePerMonth}
          text={priceCurrency.displayName}
          variant="PRIMARY"
        >
          <Menu
            items={[
              {
                displayName: euro.displayName,
                onClick: () => handleOnCurrencyChange(euro.value, typePrice),
              },
              {
                displayName: pound.displayName,
                onClick: () => handleOnCurrencyChange(pound.value, typePrice),
              },
            ]}
          />
        </SimpleDropdown>
      </HeaderContainer>
      <TypeContainer>
        <ListSingleSelectTag
          name="type_price"
          label=""
          options={[
            { value: 'price', label: 'Total Price' },
            { value: 'pricePerMonth', label: 'Per month' },
          ]}
          value={typePrice}
          onChange={(newValue: string) => {
            onChangeType(currency, newValue as TypePrice);
          }}
        />
      </TypeContainer>

      {isPricePerMonth ? (
        <DropDownRangeContainer>
          <Select
            onChange={(fromMonth) => {
              setFrom(removeNonNumericCharacters(fromMonth));
              onChangePrice(
                priceCurrency.value,
                removeNonNumericCharacters(fromMonth),
                toValue,
                typePrice,
              );
            }}
            name={'price_month_range_from'}
            value={
              fromMonth && fromMonth.selectedValue && fromMonth.selectedValue
            }
            options={fromMonth && fromMonth.options}
          />
          <span className="divider" />
          <Select
            onChange={(toMonth) => {
              setTo(removeNonNumericCharacters(toMonth));
              onChangePrice(
                priceCurrency.value,
                fromValue,
                removeNonNumericCharacters(toMonth),
                typePrice,
              );
            }}
            name={'price_month_range_to'}
            value={toMonth && toMonth.selectedValue && toMonth.selectedValue}
            options={toMonth && mapOptionsRanges(fromMonth, toMonth)}
          />
        </DropDownRangeContainer>
      ) : (
        <DropDownRangeContainer>
          <Select
            onChange={(from) => {
              setFrom(removeNonNumericCharacters(from));
              onChangePrice(
                priceCurrency.value,
                removeNonNumericCharacters(from),
                toValue,
                typePrice,
              );
            }}
            name={'price_range_from'}
            value={from && from.selectedValue && from.selectedValue}
            options={from && from.options}
          />
          <span className="divider" />
          <Select
            onChange={(to) => {
              setTo(removeNonNumericCharacters(to));
              onChangePrice(
                priceCurrency.value,
                fromValue,
                removeNonNumericCharacters(to),
                typePrice,
              );
            }}
            name={'price_range_to'}
            value={to && to.selectedValue && to.selectedValue}
            options={to && mapOptionsRanges(from, to)}
          />
        </DropDownRangeContainer>
      )}
    </div>
  );
}

export { PriceSelectMonthRange };
